import { Edit, MoreVertical, Paperclip, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ReadCaseExpense } from '@/services/api/generated/caseExpensesService';

import type { TableColumn } from '../../common/DataTable/types';
import { formatCurrency } from '../shared/formatUtils';
import { getPayeeDisplayName } from './caseExpenseUtils';
import {
  EXPENSE_TYPE_CONFIG,
  PAYMENT_METHOD_CONFIG,
  STATUS_CONFIG,
} from './constants';

interface CaseExpenseColumnsOptions {
  onEdit: (expense: ReadCaseExpense) => void;
  onDelete: (expense: ReadCaseExpense) => void;
}

/**
 * Column definitions for the case expenses table.
 */
export const createCaseExpenseColumns = ({
  onEdit,
  onDelete,
}: CaseExpenseColumnsOptions): TableColumn<ReadCaseExpense>[] => [
  {
    id: 'payee_name',
    header: 'Payee',
    accessorFn: getPayeeDisplayName,
    width: '200px',
    cell: (row) => {
      const name = getPayeeDisplayName(row);
      return (
        <span className="font-medium text-zinc-900 dark:text-white truncate block max-w-[200px]">
          {name}
        </span>
      );
    },
    sortable: true,
  },
  {
    id: 'expense_type',
    header: 'Type',
    accessorKey: 'expense_type',
    width: '100px',
    cell: (row) => {
      const config = EXPENSE_TYPE_CONFIG[row.expense_type];
      return (
        <Badge
          variant="outline"
          className={cn('font-medium', config.badgeClass)}
        >
          {config.label}
        </Badge>
      );
    },
  },
  {
    id: 'amount',
    header: 'Amount',
    accessorKey: 'amount',
    width: '120px',
    align: 'right',
    cell: (row) => {
      const amount = formatCurrency(row.amount);
      return (
        <span className="font-medium text-zinc-900 dark:text-white">
          {amount}
        </span>
      );
    },
    sortable: true,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    width: '110px',
    cell: (row) => {
      const config = STATUS_CONFIG[row.status];
      return (
        <Badge
          variant="outline"
          className={cn('font-medium', config.badgeClass)}
        >
          {config.label}
        </Badge>
      );
    },
  },
  {
    id: 'payment_method',
    header: 'Payment',
    accessorKey: 'payment_method',
    width: '140px',
    cell: (row) => {
      if (!row.payment_method) {
        return <span className="text-slate-400">-</span>;
      }
      const methodLabel = PAYMENT_METHOD_CONFIG[row.payment_method];
      const identifier = row.payment_method_identifier;
      return (
        <div className="text-slate-600">
          <span>{methodLabel}</span>
          {identifier && (
            <span className="text-slate-400 ml-1">({identifier})</span>
          )}
        </div>
      );
    },
  },
  {
    id: 'payment_date',
    header: 'Date',
    accessorKey: 'payment_date',
    width: '110px',
    cell: (row) => {
      if (!row.payment_date) {
        return <span className="text-slate-400">-</span>;
      }
      return (
        <span className="text-slate-600">
          {new Date(row.payment_date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      );
    },
    sortable: true,
  },
  {
    id: 'reason',
    header: 'Notes',
    accessorKey: 'reason',
    width: '180px',
    cell: (row) => {
      if (!row.reason) {
        return <span className="text-slate-400">-</span>;
      }
      return (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <span className="text-slate-600 truncate block max-w-[160px] cursor-default">
              {row.reason}
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="start"
            sideOffset={4}
            className="max-w-xs whitespace-pre-wrap text-sm"
          >
            {row.reason}
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: 'attachments',
    header: 'Attachments',
    width: '100px',
    align: 'center',
    cell: (row) => {
      const attachments = row.blobs ?? [];
      if (attachments.length === 0) {
        return null;
      }
      return (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center gap-1 text-slate-500 cursor-default">
              <Paperclip className="h-4 w-4" />
              <span className="text-xs">{attachments.length}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="center"
            sideOffset={4}
            className="max-w-xs text-sm"
          >
            <div className="space-y-1">
              {attachments.map((blob) => (
                <div key={blob.id as string} className="truncate max-w-[200px]">
                  {blob.name}
                </div>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    id: 'actions',
    header: '',
    width: '60px',
    align: 'center',
    cell: (row) => {
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
              }}
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row);
              }}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
  },
];
